import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import env from '@utils/env'
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube,
  Mail,
  Phone,
  MapPin,
  Clock,
  GraduationCap,
  ChevronRight,
  ArrowUp
} from 'lucide-react'

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'All Courses', href: '/courses' },
    { name: 'DevOps Roadmap', href: '/devops-roadmap' },
    { name: 'Success Stories', href: '/testimonials' },
    { name: 'Batch Schedule', href: '/batches' },
    { name: 'Contact Us', href: '/contact' }
  ]

  const courses = [
    { name: 'Linux & Networking Essentials', href: '/courses/linux-and-networking-essentials' },
    { name: 'Cloud Essentials', href: '/courses/cloud-essentials' },
    { name: 'DevOps Essentials', href: '/courses/devops-essentials' },
    { name: 'AWS Professional Architect', href: '/courses/aws-professional-architect' },
    { name: 'GCP Professional Architect', href: '/courses/gcp-professional-architect' },
    { name: 'Azure Architect', href: '/courses/azure-architect' },
    { name: 'Kubernetes Architect', href: '/courses/kubernetes-architect' },
  ]

  const resources = [
    { name: 'Blog', href: '/blog' },
    { name: 'Career Guidance', href: '/career' },
    { name: 'Interview Questions', href: '/interview-questions' },
  ]

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com/deoriginlabs', label: 'Facebook' },
    { icon: Twitter, href: 'https://twitter.com/deoriginlabs', label: 'Twitter' },
    { icon: Linkedin, href: env.linkedinUrl, label: 'LinkedIn' },
    { icon: Instagram, href: env.instagramUrl, label: 'Instagram' },
    { icon: Youtube, href: env.youtubeUrl, label: 'YouTube' }
  ]

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: `+${env.whatsappNumber}`,
      href: `tel:+${env.whatsappNumber}`
    },
    {
      icon: Mail,
      label: 'Email',
      value: env.supportEmail,
      href: `mailto:${env.supportEmail}`
    },
    {
      icon: MapPin,
      label: 'Address',
      value: 'Bangalore, India',
      href: '#'
    },
    {
      icon: Clock,
      label: 'Working Hours',
      value: 'Mon - Sat: 9:00 AM - 7:00 PM',
      href: '#'
    }
  ]

  return (
    <footer className="bg-yellow-50 text-gray-900">
      {/* Main Footer Content */}
      <div className="container-custom pt-0 pb-16 border-b border-gray-800">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center mb-2 group">
              <img
                src="/deoriginlabs-logo.png"
                alt="Deoriginlabs - DevOps & Cloud Training Institute"
                className="h-44 sm:h-52 md:h-52 lg:h-52 xl:h-52 w-auto object-contain group-hover:scale-105 transition-transform duration-200"
              />
            </Link>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              Your trusted partner for DevOps and Cloud training. 
              Transform your career with industry-recognized certifications 
              and hands-on experience.
            </p>

            <div className="space-y-3">
              {contactInfo.map((info, index) => (
                <a
                  key={index}
                  href={info.href}
                  className="flex items-center space-x-3 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <span className="text-primary-400 shrink-0">
                    <info.icon className="w-5 h-5" />
                  </span>
                  <span className="text-sm text-gray-700">{info.value}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="pt-10">
            <h3 className="font-semibold text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Courses */}
          <div className="pt-10">
            <h3 className="font-semibold text-lg mb-6">Popular Courses</h3>
            <ul className="space-y-3">
              {courses.map((course, index) => (
                <li key={index}>
                  <Link
                    to={course.href}
                    onClick={scrollToTop}
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                    <span>{course.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources & Social */}
          <div className="pt-10">
            <h3 className="font-semibold text-lg mb-6">Resources</h3>
            <ul className="space-y-3 mb-6">
              {resources.map((resource, index) => (
                <li key={index}>
                  <Link
                    to={resource.href}
                    onClick={scrollToTop}
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                    <span>{resource.name}</span>
                  </Link>
                </li>
              ))}
            </ul>

            <div>
              <h4 className="font-medium mb-4">Follow Us</h4>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center hover:bg-primary-600 hover:text-white transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter & CTA Section */}
      <div>
        <div className="container-custom py-12 border-b border-gray-800">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-2">Stay Updated</h3>
              <p className="text-gray-700">
                Subscribe to get the latest updates on new courses and offers
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-gray-800 dark:bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-primary-500 transition-colors text-white placeholder-gray-500"
              />
              <button className="btn btn-primary px-8">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div>
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 md:pr-20">
            <div className="text-gray-700 text-sm">
              © {new Date().getFullYear()} {env.appName}. All rights reserved.
            </div>
            
            <div className="flex items-center space-x-6">
              <Link to="/privacy" onClick={scrollToTop} className="text-gray-700 hover:text-gray-900 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" onClick={scrollToTop} className="text-gray-700 hover:text-gray-900 text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/refund" onClick={scrollToTop} className="text-gray-700 hover:text-gray-900 text-sm transition-colors">
                Refund Policy
              </Link>
            </div>

            <button
              onClick={scrollToTop}
              className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              <ArrowUp className="w-4 h-4" />
              <span className="text-sm">Back to top</span>
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
